import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment.dev";
import { BaseService } from "../../@core/services/base.service";
import { HttpClient } from "@angular/common/http";
import { IOrganization, IShareUser, IWorkspace } from "../../@core/models/model";
import { BehaviorSubject, Observable } from "rxjs";
import { LOGIN_METHOD } from "../models/contract-management.model";

@Injectable({ providedIn: 'root' })
export class OrganizationSetupFormService extends BaseService {

    private currentWorkspaceSubject = new BehaviorSubject<IWorkspace | null>(this.getCurrentWorkspaceFromStorage());
    public currentWorkspace: Observable<IWorkspace | null>;
    constructor(
        protected override http: HttpClient
    ) {
        super(http);
        this.currentWorkspace = this.currentWorkspaceSubject.asObservable();
    }

    apiUrl = environment.apiUrl + '/organization-setup';

    updateOrganizationSetupData(data: FormData) {
        return this.post<IOrganization>(this.apiUrl + '/update-organization', data)
    }

    fetchOrganizationSetupData() {
        return this.get<IOrganization>(this.apiUrl + '/fetch-organization')
    }

    saveLoginMethods(data: { loginMethods: LOGIN_METHOD[] }) {
        return this.post<IOrganization>(this.apiUrl + '/login-methods', data)
    }

    getSubscriptionDetails() {
        return this.get(this.apiUrl + '/fetch-subscription-details')
    }

    getWorkspaces() {
        return this.get<(IWorkspace & { sharedUsers: IShareUser[] })[]>(this.apiUrl + '/get-workspaces')
    }

    createWorkspace(data: Partial<IWorkspace>) {
        return this.post<IWorkspace>(this.apiUrl + '/create-workspace', data)
    }

    updateWorkspace(id: string, data: Partial<IWorkspace>) {
        return this.post<IWorkspace>(this.apiUrl + '/update-workspace', { id, data })
    }

    deleteWorkspace(id: string) {
        return this.delete<IWorkspace>(this.apiUrl + '/delete-workspace/' + id)
    }



    // Current Workspace Management
    private getCurrentWorkspaceFromStorage(): IWorkspace | null {
        const workspaceStr = localStorage.getItem('currentWorkspace');
        if (workspaceStr) {
            try {
                return JSON.parse(workspaceStr);
            } catch (error) {
                console.error('Error parsing current workspace from localStorage:', error);
                localStorage.removeItem('currentWorkspace');
                return null;
            }
        }
        return null;
    }

    public setCurrentWorkspace(workspace: IWorkspace): void {
        localStorage.setItem('currentWorkspace', JSON.stringify(workspace));
        this.currentWorkspaceSubject.next(workspace);
    }

    public getCurrentWorkspaceValue(): IWorkspace | null {
        return this.currentWorkspaceSubject.value;
    }

    public initializeWorkspaceFromList(workspaces: IWorkspace[]): IWorkspace | null {
        if (workspaces && workspaces.length > 0) {
            const currentWorkspace = this.getCurrentWorkspaceFromStorage();

            const isValidWorkspace = currentWorkspace &&
                workspaces.find(ws => ws._id === currentWorkspace._id);

            if (isValidWorkspace) {
                this.currentWorkspaceSubject.next(isValidWorkspace);
                return null; // No workspace needs to be set, already valid
            } else {
                const firstWorkspace = workspaces[0];
                this.setCurrentWorkspace(firstWorkspace);
                return firstWorkspace; // Return the workspace that was set
            }
        }
        return null;
    }

    public clearCurrentWorkspace(): void {
        localStorage.removeItem('currentWorkspace');
        this.currentWorkspaceSubject.next(null);
    }

    public updateContactSales(contactedSales: boolean) {
        return this.post(this.apiUrl + '/update-contact-sales', { contactedSales: contactedSales })
    }

    deleteOrganization() {
        return this.delete(this.apiUrl + '/delete-organization')
    }

    getOrgUsersPlanMap() {
        return this.get(environment.apiUrl + '/payment/org-users-plans');
    }
}
